import { ProfileTracker, PROFILE_TRACKER_STAGE_STATUS } from '../../profile/types/profile-tracker.model';
import { PROFILE_STAGES } from '../../profile/types/profile-stages.model';
import { FULL_ACCESS_MEMBERSHIP_STATUS_IDS } from '../../profile/types/membership-type-ids.model';
import { UserMembership } from '../../profile/types/user-membership.model';
import { coerceAuthTypeFk } from './auth-user-flags';

export type UserAccessState = 'ONBOARDING' | 'APPLICATION' | 'BLOCKED' | 'ALLOWED';

/** Ordered membership-application steps (stage id + route slug). */
export const MEMBERSHIP_APPLICATION_STAGES: ReadonlyArray<{ stage: PROFILE_STAGES; slug: string }> = [
  { stage: PROFILE_STAGES.APPLICATION_START, slug: 'application-start' },
  { stage: PROFILE_STAGES.APPLICATION_QUESTIONS, slug: 'application-questions' },
  { stage: PROFILE_STAGES.APPLICATION_RANKING, slug: 'application-ranking' },
  { stage: PROFILE_STAGES.APPLICATION_REVIEW, slug: 'application-review' },
  { stage: PROFILE_STAGES.APPLICATION_COMPLETE, slug: 'application-complete' },
];

export function getApplicationStageIdFromSlug(slug: string): PROFILE_STAGES | undefined {
  const entry = MEMBERSHIP_APPLICATION_STAGES.find((s) => s.slug === slug);
  return entry?.stage;
}

export function getMembershipApplicationSlugIndex(slug: string): number {
  return MEMBERSHIP_APPLICATION_STAGES.findIndex((s) => s.slug === slug);
}

/** Same pattern as PROFILE_STAGES: fixed ids aligned with backend type tables. */
export function getAllowedFullAccessMembershipStatusIds(): number[] {
  return [...FULL_ACCESS_MEMBERSHIP_STATUS_IDS];
}

/** Resolve the single membership object from profile/auth user (API: `membership` or legacy `basic_membership`). */
export function getMembershipFromUser(user: {
  membership?: UserMembership | null;
  basic_membership?: UserMembership | null;
  membership_status?: number | null;
  membership_type?: number | null;
} | null | undefined): UserMembership | null | undefined {
  if (!user) {
    return undefined;
  }
  if (user.membership) {
    return user.membership;
  }
  if (user.basic_membership) {
    return user.basic_membership;
  }
  const membership_status = coerceAuthTypeFk(user.membership_status);
  const membership_type = coerceAuthTypeFk(user.membership_type);
  if (membership_status == null && membership_type == null) {
    return undefined;
  }
  return { membership_status, membership_type };
}

/**
 * Numeric FK for membership status from the membership row (prefers scalar `membership_status`).
 * Auth may send `null` when there is no membership row — that is absent, not status 0.
 */
export function getMembershipStatusId(membership: UserMembership | null | undefined): number | undefined {
  if (membership == null) {
    return undefined;
  }
  const statusId = coerceAuthTypeFk(membership.membership_status);
  if (statusId != null) {
    return statusId;
  }
  const nested = membership.membership_status_detail;
  if (nested && nested.id != null) {
    return Number(nested.id);
  }
  return undefined;
}

/**
 * Tracker row for a given stage id, if present.
 */
export function getTrackerRowForStage(
  tracker: ProfileTracker[] | null | undefined,
  stageId: number
): ProfileTracker | undefined {
  if (!tracker?.length) {
    return undefined;
  }
  const sid = Number(stageId);
  return tracker.find((t) => Number(t.stage) === sid);
}

/**
 * Main onboarding (steps 1–17) is complete when `FINAL_CONFIRMATION` is completed.
 */
export function isMainOnboardingComplete(tracker: ProfileTracker[] | null | undefined): boolean {
  const row = getTrackerRowForStage(tracker, PROFILE_STAGES.FINAL_CONFIRMATION);
  return row?.status === PROFILE_TRACKER_STAGE_STATUS.COMPLETED;
}

/**
 * Membership application phase (18–22) is complete when `APPLICATION_COMPLETE` is completed.
 */
export function isApplicationPhaseComplete(tracker: ProfileTracker[] | null | undefined): boolean {
  const row = getTrackerRowForStage(tracker, PROFILE_STAGES.APPLICATION_COMPLETE);
  return row?.status === PROFILE_TRACKER_STAGE_STATUS.COMPLETED;
}

/**
 * Legacy name: “onboarding” for the membership wall meant both main profile onboarding and
 * application phase. Prefer `isMainOnboardingComplete` / `isApplicationPhaseComplete`.
 * @deprecated Use `isMainOnboardingComplete` and `isApplicationPhaseComplete` explicitly.
 */
export function isOnboardingTrackerComplete(tracker: ProfileTracker[] | null | undefined): boolean {
  return isMainOnboardingComplete(tracker) && isApplicationPhaseComplete(tracker);
}

/**
 * First application-phase slug the user still needs (in order), or `undefined` if none left.
 */
export function resolveNextMembershipApplicationSlug(
  tracker: ProfileTracker[] | null | undefined
): string | undefined {
  for (const { stage, slug } of MEMBERSHIP_APPLICATION_STAGES) {
    const row = getTrackerRowForStage(tracker, stage);
    if (!row || row.status !== PROFILE_TRACKER_STAGE_STATUS.COMPLETED) {
      return slug;
    }
  }
  return undefined;
}

/**
 * Single source of truth for membership wall.
 * `allowedFullAccessStatusIds` should be `getAllowedFullAccessMembershipStatusIds()` (fixed enum ids).
 */
export function resolveUserAccessState(
  membership: UserMembership | null | undefined,
  tracker: ProfileTracker[] | null | undefined,
  allowedFullAccessStatusIds: number[]
): UserAccessState {
  if (!isMainOnboardingComplete(tracker)) {
    return 'ONBOARDING';
  }
  if (!isApplicationPhaseComplete(tracker)) {
    return 'APPLICATION';
  }
  if (!isMembershipAllowed(membership, allowedFullAccessStatusIds)) {
    return 'BLOCKED';
  }
  return 'ALLOWED';
}

/**
 * Access allowed when `membership_status` FK is in the allowed-id set (accepted / trial).
 */
export function isMembershipAllowed(
  membership: UserMembership | null | undefined,
  allowedFullAccessStatusIds: number[]
): boolean {
  if (membership == null || !allowedFullAccessStatusIds.length) {
    return false;
  }
  const statusId = getMembershipStatusId(membership);
  if (statusId == null) {
    return false;
  }
  return allowedFullAccessStatusIds.includes(statusId);
}

/**
 * Normalized path without query string; no trailing slash except root.
 */
export function normalizeRoutePath(url: string): string {
  const path = url.split('?')[0].split('#')[0];
  if (path.length > 1 && path.endsWith('/')) {
    return path.slice(0, -1);
  }
  return path;
}

/** Paths that must not be used as `redirecturl` after login (avoids loops and useless returns). */
const INVALID_POST_LOGIN_REDIRECT_PREFIXES = ['/login', '/join', '/account/password/reset'] as const;

function isInvalidPostLoginPath(normalizedPath: string): boolean {
  return INVALID_POST_LOGIN_REDIRECT_PREFIXES.some(
    (p) => normalizedPath === p || normalizedPath.startsWith(p + '/')
  );
}

/**
 * Returns a safe in-app return path for post-login navigation, or `undefined` to fall back to `/feed`.
 * Strips auth entry URLs (`/login`, `/join`, password reset) and foreign origins.
 */
export function sanitizePostLoginRedirectParam(raw: string | undefined | null): string | undefined {
  if (raw == null) {
    return undefined;
  }
  let s = String(raw).trim();
  if (!s) {
    return undefined;
  }
  try {
    s = decodeURIComponent(s);
  } catch {
    // keep s
  }
  s = s.trim();
  if (!s) {
    return undefined;
  }

  if (s.startsWith('http://') || s.startsWith('https://')) {
    if (typeof window === 'undefined' || !window.location?.origin) {
      return undefined;
    }
    try {
      const u = new URL(s);
      if (u.origin !== window.location.origin) {
        return undefined;
      }
      const pathAndQuery = `${u.pathname}${u.search || ''}`;
      return sanitizePostLoginRedirectParam(pathAndQuery);
    } catch {
      return undefined;
    }
  }

  const pathOnly = normalizeRoutePath(s.split('?')[0].split('#')[0]);
  if (!pathOnly) {
    return undefined;
  }
  if (isInvalidPostLoginPath(pathOnly)) {
    return undefined;
  }

  if (!s.startsWith('/')) {
    s = '/' + s.replace(/^\/+/, '');
  }
  return s;
}

/**
 * Routes reachable while membership is BLOCKED (extended whitelist per plan).
 */
export function isAllowedRouteWhileBlocked(normalizedPath: string): boolean {
  const prefixes = [
    '/profile',
    '/welcome',
    '/membership-review',
    '/membership-application',
    '/add-email',
    '/change-email',
    '/verify-email',
    '/add-phone',
    '/change-phone',
    '/verify-phone-number',
    '/account/social/connect',
    '/account/email/verification',
    '/organisation',
  ];
  return prefixes.some((p) => normalizedPath === p || normalizedPath.startsWith(p + '/'));
}
